<template>
  <div class="header-container">
    <div class="header-detail-container">
      <h1 class="header-code pb-2">
        <u
          v-tooltip="`Click to Copy`"
          v-clipboard:copy="consignment.con_full"
          v-clipboard:success="() => $message.success('Consignment copied to clipboard')"
        >{{ consignment.con_full }}</u> for
        <router-link
          v-if="consignment.team"
          :to="{ name: 'customerservice-accounts-account', params: { accountId: consignment.team.account.id } }"
        >
          {{ $_.get(consignment, 'team.account.name', '-') }}
        </router-link>
      </h1>
      <el-row :gutter="15">
        <el-col :span="6">
          <p>
            <b>Reference</b><br>
            {{ (consignment.con_ref || '-') }}
          </p>

          <p>
            <b>Created At</b><br>
            {{ consignment.created_at ? $moment(consignment.created_at).format('llll') : '-' }}
          </p>
        </el-col>

        <el-col :span="4">
          <p>
            <b>Team</b><br>
            <span class="text-uppercase">{{ $_.get(consignment, 'team.name', '-') }}</span>
          </p>

          <p>
            <b>Last Modified At</b><br>
            {{ consignment.updated_at ? $moment(consignment.updated_at).format('llll') : '-' }}
          </p>
        </el-col>

        <el-col :span="6">
          <p>
            <b>Portfolio</b><br>
            {{ $_.get(consignment, 'account.portfolio.profile.name_display', '-') }}
          </p>

          <p>
            <b>Investigator</b><br>
            {{ $_.get(consignment, 'account.investigator.name_display', '-') }}
          </p>
        </el-col>

        <el-col :span="8">
          <p>
            <b>Total Items, Weight, &amp; Volume</b><br>
            {{ `${itemsCount} ${itemsCount > 0 ? 'items' : 'item'}` }}, {{ itemsWeight }}kg, {{ itemsVolume }}m<sup>3</sup>
          </p>
          <p>
            <b>Information</b><br>
            <el-popover
              v-if="extData.cancelled_by && consignment.con_type === -6"
              placement="top-start"
              title="Cancelled By"
              width="200"
              trigger="hover"
              :content="cancelledBy"
            >
              <el-tag
                slot="reference"
                :type="typeTag"
              >
                {{ typeText | titleize }}
              </el-tag>
            </el-popover>
            <el-tag
              v-else
              :type="typeTag"
            >
              {{ typeText | titleize }}
            </el-tag>
            &nbsp;
            <el-tag
              :type="validatedTag"
            >
              Validated
            </el-tag>
            &nbsp;
            <el-tag
              :type="manifestedTag"
              @click.once="createManifest(consignment)"
            >
              Manifested
            </el-tag>
            &nbsp;
            <el-tag
              :type="transmittedTag"
            >
              Transmitted
            </el-tag>
            &nbsp;
            <el-tag
              v-if="consignment.enquiries.length > 0"
              type="warning"
            >
              {{ consignment.enquiries.length }} {{ consignment.enquiries.length > 1 ? "Enquiries" : "Enquiry" }}
            </el-tag>
            &nbsp;
            <el-tag
              v-if="consignment.application"
              type="default"
            >
              {{ consignment.application }}
            </el-tag>
            &nbsp;
            <el-tag
              v-if="consignment.watchlists && consignment.watchlists.length > 0"
              type="warning"
            >
              {{ consignment.watchlists.length }} {{ consignment.watchlists.length > 1 ? "Watchers" : "Watcher" }}
            </el-tag>
            <!-- <el-tag
              type="danger"
            >
              Reconcilled
            </el-tag>
            &nbsp;
            <el-tag
              type="danger"
            >
              Invoiced
            </el-tag> -->
          </p>
        </el-col>
      </el-row>
    </div>
  </div>
</template>

<script>
import { createManifest } from '../LegacyFunctions'

export default {
  props: {
    consignment: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      statusTags: {
        '-7': 'danger',
        '-6': 'danger',
        '-5': 'warning',
        '-4': 'warning',
        '-3': 'warning',
        '-2': 'warning',
        '-1': 'danger',
        '0': 'success',
        '1': 'info',
        '2': 'warning',
        '3': 'info'
      },
      statusCodes: {
        '-7': 'Hidden / Ignored',
        '-6': 'Cancelled / Didn\'t Travel',
        '-5': 'Created by Enquiry',
        '-4': 'Deleted by Recon',
        '-3': 'Updated by Recon',
        '-2': 'Created by Recon',
        '-1': 'Deleted',
        '0': 'Regular',
        '1': 'Return',
        '2': 'Import',
        '3': 'Quoted'
      }
    }
  },
  computed: {
    extData () {
      try {
        return JSON.parse(this.consignment.con_ext_data)
      } catch (e) {
        return {}
      }
    },
    cancelledBy () {
      return this.extData.cancelled_by ? this.extData.cancelled_by : null
    },
    itemsWeight () {
      let weight = 0

      if (this.consignment.items.length > 0) {
        this.consignment.items.map(item => {
          weight += item.item_weight ? parseFloat(item.item_weight) : 0
        })
      }

      return weight
    },
    itemsVolume () {
      let volume = 0

      if (this.consignment.items.length > 0) {
        this.consignment.items.map(item => {
          volume += item.item_cube ? parseFloat(item.item_cube * item.item_qty) : 0
        })
      }

      return volume
    },
    itemsCount () {
      let count = 0

      if (this.consignment.items.length > 0) {
        count = this.consignment.items
          .map(i => i.item_qty)
          .reduce((total, qty) => total + parseInt(qty), 0)
      }

      return count
    },
    typeTag () {
      return this.consignment.con_type in this.statusTags ? this.statusTags[this.consignment.con_type] : 'info'
    },
    typeText () {
      return this.consignment.con_type in this.statusCodes ? this.statusCodes[this.consignment.con_type] : '-'
    },
    validatedTag () {
      return this.consignment.con_validated > 0 ? 'success' : 'danger'
    },
    manifestedTag () {
      return this.consignment.edi_id > 0 ? 'success' : 'danger'
    },
    transmittedTag () {
      return this.consignment.edi !== null && this.consignment.edi.export_status === 3 ? 'success' : 'danger'
    },
    reconciledTagTag () {
      return this.consignment.con_type in this.statusTags ? this.statusTags[this.consignment.con_type] : 'info'
    },
    invoicedTag () {
      return this.consignment.con_type in this.statusTags ? this.statusTags[this.consignment.con_type] : 'info'
    }
  },
  methods: {
    async manifestConsignment () {
      let manifest = await createManifest(this.consignment)
      if (manifest) {
        this.$message.success({ message: 'Manifest Created' })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import "variables";
.header-container {
  display: flex;
  align-items: center;
  .header-status {
    padding: 0px 50px 0px 30px;
    .header-text {
      font-size: 2rem; text-align: center;
    }
    .status-0 {
      color: $info;
    }
    .status--1 {
      color: $danger;
    }
    .status-1 {
      color: $warning;
    }
    .status-2 {
      color: $danger;
    }
    .status-3 {
      color: $warning;
    }
    .status-processing {
      color: $warning;
    }
    .status-processed {
      color: $primary;
    }
    .status-confirmed {
      color: $success;
    }
  }
  .header-detail-container {
    flex: 1;
    .header-code {
      cursor: pointer;
      font-size: 2rem; margin-bottom: 5px;
    }
  }
  .created-by-open360 {
    background-color: #E0E6F9;
    border-color: #284ED524;
    color: #284ED5B5;
  }
}
</style>
